import {
  createApiRef,
  DiscoveryApi,
  IdentityApi,
  FetchApi,
} from '@backstage/core-plugin-api';

export interface ManifestFetchResponse {
  entity: {
    kind: string;
    namespace: string;
    name: string;
    title?: string;
  };
  manifest: {
    yaml: string;
    sha: string;
  };
  location: {
    host: string;
    owner: string;
    repo: string;
    branch: string;
    filePath: string;
    apiBaseUrl: string;
    webBaseUrl: string;
    username?: string;
    email?: string;
  };
}

export interface ManifestPullRequestResponse {
  branchName: string;
  pullRequestNumber: number;
  pullRequestUrl: string;
  commitSha: string;
}

export interface ManifestEditorApi {
  getManifest(entityRef: string): Promise<ManifestFetchResponse>;
  createPullRequest(options: {
    entityRef: string;
    manifest: string;
    title?: string;
    description?: string;
  }): Promise<ManifestPullRequestResponse>;
}

export const manifestEditorApiRef = createApiRef<ManifestEditorApi>({
  id: 'plugin.manifest-editor.service',
});

export class ManifestEditorClient implements ManifestEditorApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;
  private readonly fetchApi: FetchApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
    fetchApi: FetchApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
    this.fetchApi = options.fetchApi;
  }

  private async request<T>(
    path: string,
    init?: RequestInit,
  ): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('manifest-editor');
    const { token } = await this.identityApi.getCredentials();

    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        ...(init?.headers ?? {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Manifest editor request failed (${response.status}): ${message}`,
      );
    }

    return (await response.json()) as T;
  }

  async getManifest(entityRef: string): Promise<ManifestFetchResponse> {
    const search = new URLSearchParams({ entityRef });
    return this.request<ManifestFetchResponse>(`/entity?${search.toString()}`);
  }

  async createPullRequest(options: {
    entityRef: string;
    manifest: string;
    title?: string | undefined;
    description?: string | undefined;
  }): Promise<ManifestPullRequestResponse> {
    return this.request<ManifestPullRequestResponse>(
      '/entity/pull-request',
      {
        method: 'POST',
        body: JSON.stringify(options),
      },
    );
  }
}
